'use client'

import React, { useId, useMemo } from 'react'

import ReactSelect, { components } from 'react-select'

import { Box, Text, chakra } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { ChevronDownIcon } from '../Icon/ChevronDownIcon'
import { CloseIcon } from '../Icon/CloseIcon'
import { warningTextStyles } from '../TextInput/TextInput.styles'
import { errorColorFor } from '../shared/errorColor'
import {
  fieldLabelStyles,
  fieldLabelWrapperStyles,
} from '../shared/fieldLabelStyles'
import {
  createSelectStyles,
  primaryActionTokensFor,
  selectBoxTokens,
} from './Select.styles'

export type SelectOption = {
  label: string
  value: string
}

export type SelectPrimaryAction = {
  header?: string
  label: string
  onClick: () => void
}

export interface SelectIProps {
  options: SelectOption[]
  value: SelectOption | null
  onChange: (option: SelectOption | null) => void
  placeholder?: string
  /** Optional label rendered above the select. */
  label?: string
  /** Accessible name for compact selects that do not render a visible label. */
  'aria-label'?: string
  /**
   * ID given to the underlying search input so `label` can point at it with
   * `htmlFor`. Omit and one is generated; pass it when an outside label or a
   * test needs to address the control directly.
   */
  inputId?: string
  isLoading?: boolean
  isDisabled?: boolean
  colorScheme?: ColorScheme
  primaryAction?: SelectPrimaryAction
  /** Shows a clear (×) affordance once a value is selected. */
  isClearable?: boolean
  /** Set false for a plain picker with no type-to-filter input. */
  isSearchable?: boolean
  /**
   * Fired as the user types. Pair with server-side option loading for pickers
   * whose full option set is too large to send up front.
   */
  onInputChange?: (value: string) => void
  /** Replaces the default "No options" text; return null to render nothing. */
  noOptionsMessage?: (obj: { inputValue: string }) => string | null
  /**
   * Overrides which options a typed query matches. Without it react-select
   * filters on `label` alone, so any picker that searches a second field
   * (an address, an external id) must supply this.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterOption?: (option: any, rawInput: string) => boolean
  /**
   * Overrides how an option (and the selected value) renders. Without it
   * react-select falls back to a bare `option.label` string, so any picker
   * whose options carry more than a label (a subtitle, a match-reason cue)
   * must supply this.
   */
  formatOptionLabel?: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    option: any,
    formatOptionLabelMeta: { context: 'menu' | 'value'; inputValue: string }
  ) => React.ReactNode
  /** Paints the error border and reveals `warningText`. */
  hasWarning?: boolean
  warningText?: string
  'data-testid'?: string
}

// Same glyph, size, color, and open-rotation as the MultiSelectInput chevron
// so every dropdown trigger in a form reads identically.
const DropdownIndicator = (props: any) => (
  <components.DropdownIndicator {...props}>
    <Box
      as='span'
      display='inline-flex'
      transform={
        props.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)'
      }
      transition='transform 0.15s ease'
    >
      <ChevronDownIcon height={24} width={24} />
    </Box>
  </components.DropdownIndicator>
)

// react-select's default clear affordance is a bare SVG on its own palette;
// this keeps it on the same glyph and token as every other field adornment.
const makeClearIndicator = (colorScheme: ColorScheme) =>
  function ClearIndicator(props: any) {
    return (
      <components.ClearIndicator {...props}>
        <Box alignItems='center' as='span' display='inline-flex'>
          <CloseIcon colorScheme={colorScheme} size={14} />
        </Box>
      </components.ClearIndicator>
    )
  }

const makePrimaryActionMenuList = (
  primaryAction: SelectPrimaryAction,
  colorScheme: ColorScheme
) =>
  function PrimaryActionMenuList({ children, ...props }: any) {
    const pt = primaryActionTokensFor(colorScheme)
    const [isHovered, setIsHovered] = React.useState(false)

    return (
      <components.MenuList {...props}>
        <Box
          borderBottomColor={pt.menuDivider}
          borderBottomWidth='1px'
          mb='4px'
          pb='4px'
        >
          {primaryAction.header && (
            <Text
              bg={pt.bgSectionHeader}
              color={pt.textSecondary}
              fontFamily='Soehne'
              fontSize='11px'
              fontWeight='600'
              letterSpacing='0.08em'
              pb='4px'
              pt='8px'
              px='12px'
              textTransform='uppercase'
              userSelect='none'
            >
              {primaryAction.header}
            </Text>
          )}
          <Box
            bg={isHovered ? pt.optionHover : 'transparent'}
            color={pt.textPrimary}
            cursor='pointer'
            fontFamily='Soehne'
            fontSize='11px'
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                primaryAction.onClick()
                props.selectProps.onMenuClose?.()
              }
            }}
            onMouseDown={(e) => {
              e.preventDefault()
              primaryAction.onClick()
              props.selectProps.onMenuClose?.()
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            px='12px'
            py='8px'
            role='button'
            tabIndex={0}
            userSelect='none'
          >
            {primaryAction.label}
          </Box>
        </Box>
        {children}
      </components.MenuList>
    )
  }

export const Select = ({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  label,
  'aria-label': ariaLabel,
  inputId,
  isLoading = false,
  isDisabled = false,
  colorScheme = 'dark',
  primaryAction,
  isClearable = false,
  isSearchable = true,
  onInputChange,
  noOptionsMessage,
  filterOption,
  formatOptionLabel,
  hasWarning = false,
  warningText,
  'data-testid': dataTestId,
}: SelectIProps) => {
  const styles = useMemo(() => createSelectStyles(colorScheme), [colorScheme])
  const t = selectBoxTokens(colorScheme)
  const generatedInputId = useId()
  const resolvedInputId = inputId ?? generatedInputId

  const MenuList = useMemo(
    () =>
      primaryAction
        ? makePrimaryActionMenuList(primaryAction, colorScheme)
        : components.MenuList,
    [primaryAction, colorScheme]
  )

  const ClearIndicator = useMemo(
    () => makeClearIndicator(colorScheme),
    [colorScheme]
  )

  const errorBorder = errorColorFor(colorScheme)

  const control = (
    <Box
      _focusWithin={{ borderColor: t.focusRing }}
      _hover={isDisabled || hasWarning ? undefined : { borderColor: t.border }}
      bg={t.bg}
      borderColor={hasWarning ? errorBorder : t.border}
      borderWidth='1px'
      cursor={isDisabled ? 'not-allowed' : undefined}
      data-testid={dataTestId}
      minH='36px'
      opacity={isDisabled ? 0.5 : 1}
      position='relative'
      px='10px'
      py='4px'
      transition='border-color 0.15s'
      {...(hasWarning && warningText ? { pt: '14px' } : {})}
    >
      {hasWarning && warningText && (
        <Box {...warningTextStyles(colorScheme)}>{warningText}</Box>
      )}
      <ReactSelect
        aria-label={ariaLabel}
        classNamePrefix='react-select'
        components={{
          ClearIndicator,
          DropdownIndicator,
          IndicatorSeparator: null,
          MenuList,
        }}
        inputId={resolvedInputId}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isLoading={isLoading}
        isSearchable={isSearchable}
        menuPortalTarget={
          typeof document !== 'undefined' ? document.body : null
        }
        menuPosition='fixed'
        onChange={(opt) => onChange(opt as SelectOption | null)}
        options={options}
        placeholder={placeholder}
        styles={styles}
        value={value}
        {...(onInputChange ? { onInputChange } : {})}
        {...(noOptionsMessage ? { noOptionsMessage } : {})}
        {...(filterOption ? { filterOption } : {})}
        {...(formatOptionLabel ? { formatOptionLabel } : {})}
      />
    </Box>
  )

  if (!label) return control

  return (
    <Box {...fieldLabelWrapperStyles()}>
      <chakra.label
        htmlFor={resolvedInputId}
        {...fieldLabelStyles(colorScheme)}
      >
        {label}
      </chakra.label>
      {control}
    </Box>
  )
}
